import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { LAYER_STEP, useLayer } from './overlayLayer';

// A small custom dropdown — the Phase 2 submodel filter, and the catalog's notice and
// submodel filters. Custom rather than a native <select> for ONE reason: a native
// select's open menu is drawn by the OS and ignores CSS, so its options can't be centred,
// which is the whole ask. Everything else (click-outside, Escape, the current value) is
// kept minimal.
export function CenteredSelect({
  value,
  options,
  onChange,
  ariaLabelId,
  ariaLabel,
  disabled = false,
  readOnly = false,
  busy = false,
  look = 'field',
  block = false,
  prefix,
  big = false,
}: {
  value: string;
  // `tag`: a short mark the option is known by (a submodel's number), printed apart from its
  // label — beside the prefix on the button, in a badge in the list.
  // `icon`: a mark before the label, on the button and in the list (a model kept, say).
  options: { value: string; label: string; tag?: string; icon?: ReactNode }[];
  onChange: (value: string) => void;
  // Either a label element to point at, or the label itself when the control has no
  // visible caption of its own (the catalog's filters).
  ariaLabelId?: string;
  ariaLabel?: string;
  // Shown but not usable — for a filter that depends on another one being set. Greyed
  // rather than hidden, so the control still says it exists and what it would do.
  disabled?: boolean;
  // GREYED BUT OPENABLE: the list shows what the choices are, each greyed and unpickable — for
  // a choice that means nothing on the page you are on (see SubmodelPicker).
  readOnly?: boolean;
  // AN ANSWER IS ON ITS WAY INTO THIS CONTROL. It spins in place of its value and cannot be
  // opened, which is a different state from `disabled` and has to look like one: disabled
  // says "not for you", busy says "wait". The value is HIDDEN rather than dimmed behind the
  // spinner, because what is in it is about to be replaced and reading the old one while the
  // new one lands is the confusion this exists to prevent.
  busy?: boolean;
  // SMALL AND ROUND, for a control that sits beside a heading rather than in a form: sized
  // to its value, and its list as wide as the longest option rather than as the button.
  look?: 'field' | 'pill';
  // A PILL IN THE PAGE'S OWN TEXT SIZE, for one that names what a card shows (see PartsPhase's
  // modelPicker): the small pill's xs read as a caption.
  big?: boolean;
  // AS WIDE AS ITS CONTAINER, the value wrapping onto as many lines as it needs, and its list
  // no wider: for a narrow column (the sidebar) that clips anything that pokes out of it.
  block?: boolean;
  // What the control IS, inside it before the value, where no label sits beside it.
  prefix?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // One layer above whatever it was opened in — a modal over a modal included (see overlayLayer).
  const z = useLayer() + LAYER_STEP;
  // WHERE THE LIST STANDS, on the page and not inside the control. It was drawn under the button
  // inside whatever held it, and a modal a few lines tall (a generator's settings) cut it off
  // after its first option. On document.body it opens over anything; placed under the button,
  // and above it where the window has no room below. `dark`: the button sits in the one subtree
  // that carries the dark class (the sidebar), which the body is not inside.
  const [place, setPlace] = useState<{ left: number; width: number; top?: number; bottom?: number; dark: boolean } | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const at = () => {
      const b = ref.current?.getBoundingClientRect();
      if (!b) return;
      const below = window.innerHeight - b.bottom;
      const up = below < 200 && b.top > below;
      setPlace({
        left: b.left, width: b.width,
        ...(up ? { bottom: window.innerHeight - b.top + 4 } : { top: b.bottom + 4 }),
        dark: !!ref.current?.closest('.dark'),
      });
    };
    at();
    // Followed while it is open: the page or the modal scrolling moves the button.
    window.addEventListener('scroll', at, true);
    window.addEventListener('resize', at);
    return () => {
      window.removeEventListener('scroll', at, true);
      window.removeEventListener('resize', at);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const current = selected?.label ?? '';
  // A block with a prefix is a CARD: its name and tag as a small heading, the value under it.
  const stacked = block && !!prefix;

  // A menu left open when the call lands would be a list of options over a value that
  // changed underneath it.
  useEffect(() => {
    if (busy) setOpen(false);
  }, [busy]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={ariaLabelId}
        aria-label={ariaLabel}
        disabled={disabled || busy}
        aria-busy={busy}
        onClick={() => setOpen((o) => !o)}
        // TWO LOOKS: the form field, and the pill — white and small, in both themes, for a
        // control that sits on the page or in the sidebar (which forces a dark context).
        // A block pill wraps, so its corners are those of a card rather than a capsule.
        className={`flex items-center gap-2 border transition-colors ${block ? 'w-full' : ''} ${
          look === 'pill'
            ? `${block ? `rounded-2xl text-center ${stacked ? 'px-3 py-2' : 'px-3 py-1'}` : 'rounded-full px-3 py-1'} border-slate-300 bg-white ${big ? 'text-sm' : 'text-xs'} font-semibold text-slate-800 shadow-sm`
            : 'w-full min-w-[18rem] rounded-lg border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'
        } ${
          disabled && !busy
            ? 'cursor-not-allowed opacity-50'
            : readOnly && !busy
              ? 'opacity-50'
            : busy
              ? 'cursor-progress'
              : 'hover:border-blue-500 dark:hover:border-blue-500'
        }`}
      >
        {/* flex-1 + text-center: the value sits centred, the chevron pinned right. The
            spinner takes the value's place rather than sitting beside it, so the control
            keeps its width and the row does not reflow when an answer lands. */}
        {/* Inline, on the value's baseline and in its colour and size: one line, read as
            one. Stacked (see `stacked`), a muted heading over the value. */}
        {stacked ? (
          <span className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-widest">
              {prefix}
              {selected?.tag && <span className="ml-1.5">{selected.tag}</span>}
            </span>
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              : <span className="text-[13px] leading-snug">{current}</span>}
          </span>
        ) : (
          <span className={`flex min-w-0 flex-1 ${block ? 'flex-wrap justify-center' : 'justify-center'} ${prefix ? 'items-baseline' : 'items-center'}`}>
            {prefix && (
              <span className="mr-2 uppercase tracking-wider">{prefix}</span>
            )}
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              : <span className="inline-flex items-center gap-1.5">{selected?.icon}{current}</span>}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && place && createPortal(
        <div className={place.dark ? 'dark' : undefined}>
        <ul
          ref={listRef}
          role="listbox"
          style={{
            position: 'fixed', top: place.top, bottom: place.bottom, zIndex: z,
            // Centred under the button, as wide as its longest option, for a pill; the button's
            // own width otherwise.
            ...(look === 'pill' && !block
              ? { left: place.left + place.width / 2, transform: 'translateX(-50%)' }
              : { left: place.left, width: place.width }),
          }}
          className={`max-h-64 overflow-auto rounded-lg border border-slate-300 bg-white py-1 shadow-lg ${
            look === 'pill' ? '' : 'dark:border-slate-600 dark:bg-slate-900'
          } ${look === 'pill' && !block ? 'w-max' : ''}`}
        >
          {options.map((o) => (
            <li key={o.value || '__none__'} role="option" aria-selected={o.value === value}>
              <button
                type="button"
                disabled={readOnly}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                // White in both themes for the pill, like its button.
                className={`${o.tag ? 'flex items-center gap-2 text-left' : 'block text-center'} w-full px-3 py-1.5 text-sm transition-colors ${readOnly ? 'cursor-not-allowed opacity-50' : ''} ${
                  o.value === value
                    ? `bg-blue-50 font-semibold text-blue-700 ${look === 'pill' ? '' : 'dark:bg-blue-500/10 dark:text-blue-300'}`
                    : `text-slate-700 enabled:hover:bg-slate-100 ${look === 'pill' ? '' : 'dark:text-slate-200 dark:enabled:hover:bg-slate-800'}`
                }`}
              >
                {o.tag && (
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                      o.value === value ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {o.tag}
                  </span>
                )}
                {o.icon ? <span className="inline-flex items-center gap-1.5">{o.icon}{o.label}</span> : o.label}
              </button>
            </li>
          ))}
        </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
